
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, ChevronDown, Facebook, Instagram } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../lib/utils';
import { companyInfo, services } from '../data';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Forside', path: '/' },
    { 
      name: 'Ydelser', 
      path: '/ydelser',
      dropdown: services.map(s => ({ name: s.title, path: `/ydelser/${s.slug}` }))
    },
    { name: 'Om os', path: '/om-os' },
    { 
      name: 'Galleri', 
      path: '/galleri',
      dropdown: [
        { name: 'Billedgalleri', path: '/galleri' },
        { name: 'Før & Efter', path: '/galleri/foer-og-efter' }
      ]
    },
    { name: 'Kontakt', path: '/kontakt' },
  ];

  return (
    <header className="fixed w-full z-50">
      {/* Top Bar */}
      <div className={cn(
        "bg-gray-50 text-gray-600 py-2 text-xs font-semibold transition-all duration-300 hidden md:block border-b border-gray-100",
        scrolled ? "-translate-y-full opacity-0 h-0" : "translate-y-0 opacity-100"
      )}>
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center gap-6">
            <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-2 hover:text-green-700 transition-colors">
              <Mail size={14} className="text-green-600" />
              {companyInfo.email}
            </a>
            <div className="flex items-center gap-2">
              <Phone size={14} className="text-green-600" />
              {companyInfo.phone}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-green-600">📍</span>
              Blokhusvej 25, 9490 Pandrup
            </div>
            <div className="h-4 w-[1px] bg-gray-200 mx-2" />
            <a 
              href={companyInfo.facebook} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:text-green-700 transition-colors"
              title="Følg os på Facebook"
            >
              <Facebook size={14} className="text-blue-600" />
              <span>Facebook</span>
            </a>
            <div className="h-4 w-[1px] bg-gray-200 mx-1" />
            <a 
              href={companyInfo.instagram} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:text-green-700 transition-colors"
              title="Følg os på Instagram"
            >
              <Instagram size={14} className="text-pink-600" />
              <span>Instagram</span>
            </a>
          </div>
        </div>
      </div>

      <nav className={cn(
        "transition-all duration-500 w-full",
        scrolled 
          ? "bg-white shadow-xl py-3 border-b border-gray-100" 
          : "bg-white py-5 border-b border-gray-50"
      )}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 group">
              <img 
                src="/images/smithlogo.png" 
                alt={companyInfo.name} 
                className={cn(
                  "h-12 md:h-16 w-auto transition-all duration-500",
                  !scrolled && "scale-110 md:scale-125"
                )}
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center space-x-1">
              {navLinks.map((link) => (
                <div 
                  key={link.path} 
                  className="relative group"
                  onMouseEnter={() => link.dropdown && setActiveDropdown(link.name)}
                  onMouseLeave={() => link.dropdown && setActiveDropdown(null)}
                >
                    {link.dropdown ? (
                      <span
                        className={cn(
                          "px-4 py-2 rounded-full text-sm font-bold transition-all duration-300 flex items-center gap-1 cursor-default",
                          location.pathname.startsWith(link.path)
                            ? "bg-green-50 text-green-700"
                            : "text-gray-600 hover:text-green-700 hover:bg-green-50"
                        )}
                      >
                        {link.name}
                        <ChevronDown size={14} className={cn("transition-transform duration-300", activeDropdown === link.name && "rotate-180")} />
                      </span>
                    ) : (
                      <Link
                        to={link.path}
                        className={cn(
                          "px-4 py-2 rounded-full text-sm font-bold transition-all duration-300 flex items-center gap-1",
                          location.pathname === link.path
                            ? "bg-green-50 text-green-700"
                            : "text-gray-600 hover:text-green-700 hover:bg-green-50"
                        )}
                      >
                        {link.name}
                      </Link>
                    )}

                  {link.dropdown && (
                    <AnimatePresence>
                      {activeDropdown === link.name && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="absolute top-full left-0 mt-2 w-56 bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 overflow-hidden"
                        >
                          {link.dropdown.map((sublink) => (
                            <Link
                              key={sublink.path}
                              to={sublink.path}
                              className={cn(
                                "block px-5 py-3 text-sm font-semibold transition-colors",
                                location.pathname === sublink.path
                                  ? "bg-green-50 text-green-700"
                                  : "text-gray-600 hover:bg-gray-50 hover:text-green-700"
                              )}
                              onClick={() => setActiveDropdown(null)}
                            >
                              {sublink.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </div>
              ))}
              <div className="pl-4 ml-4 border-l border-gray-200 h-8 hidden xl:block" />
              <a
                href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                className={cn(
                  "ml-4 px-1.5 py-2 rounded-full text-xs font-black transition-all duration-300 flex items-center gap-1 shadow-lg",
                  "bg-green-600 text-white hover:bg-green-700 shadow-[#1b4c2e]/10"
                )}
              >
                <Phone size={14} />
                Ring op nu
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className={cn(
                "lg:hidden p-2 rounded-xl transition-colors bg-gray-100 text-gray-900"
              )}
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Nav Overlay */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="absolute top-full left-0 w-full bg-white shadow-2xl border-t border-gray-100 lg:hidden"
            >
              <div className="flex flex-col p-6 space-y-2">
                {navLinks.map((link) => (
                  <div key={link.path} className="flex flex-col">
                    {link.dropdown ? (
                      <div
                        className={cn(
                          "text-lg font-bold p-3 rounded-xl flex items-center justify-between text-gray-900"
                        )}
                      >
                        {link.name}
                        <ChevronDown size={18} />
                      </div>
                    ) : (
                      <Link
                        to={link.path}
                        className={cn(
                          "text-lg font-bold p-3 rounded-xl transition-colors flex items-center justify-between",
                          location.pathname === link.path 
                            ? "bg-green-50 text-green-700" 
                            : "text-gray-900 hover:bg-gray-50"
                        )}
                        onClick={() => setIsOpen(false)}
                      >
                        {link.name}
                      </Link>
                    )}
                    
                    {link.dropdown && (
                      <div className="ml-4 flex flex-col border-l-2 border-green-100 mt-1 mb-2">
                        {link.dropdown.map((sublink) => (
                          <Link
                            key={sublink.path}
                            to={sublink.path}
                            className={cn(
                              "text-sm font-bold p-3 pl-6 rounded-r-xl transition-colors",
                              location.pathname === sublink.path
                                ? "text-green-700 bg-green-50/50"
                                : "text-gray-500 hover:text-green-700 hover:bg-gray-50"
                            )}
                            onClick={() => setIsOpen(false)}
                          >
                            {sublink.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <div className="pt-4 mt-4 border-t border-gray-100 flex justify-center">
                  <a
                    href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                    className="flex items-center justify-center gap-3 bg-green-600 text-white py-2 px-4 rounded-xl font-bold text-sm w-[289px]"
                  >
                    <Phone size={16} />
                    {companyInfo.phone}
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Navbar;
